import http from './http'
import CryptoJS from 'crypto-js'
import { getDeviceFingerprint, getSessionId } from '@/utils/fingerprint'

// MD5加密密码
const encryptPassword = (password) => {
  return CryptoJS.MD5(password).toString()
}

export const getTurnstileConfig = () => {
  return http.get('/auth/turnstile-config', { skipAuth: true })
}

/**
 * 用户登录
 * @param {string} username - 用户名
 * @param {string} password - 密码（明文）
 * @param {string} turnstileToken - Cloudflare Turnstile 验证token
 * @returns {Promise}
 */
export const login = async (username, password, turnstileToken) => {
  const encryptedPassword = encryptPassword(password)

  return http.post('/auth/login', {
    account: username,
    password: encryptedPassword,
    loginType: 'web',
    deviceFingerprint: await getDeviceFingerprint(),
    sessionId: getSessionId(),
    turnstileToken,
    rememberMe: false
  })
}

/** Verify a pending Web or desktop login with the authenticator code. */
export const verifyTwoFactorLogin = (challengeId, code) => {
  return http.post('/auth/login/2fa', { challengeId, code }, { skipAuth: true })
}

/** Read the current user's authenticator enrollment state. */
export const getTwoFactorStatus = () => http.get('/auth/2fa/status')

/** Start a temporary authenticator enrollment. */
export const beginTwoFactorSetup = () => http.post('/auth/2fa/setup')

/** Confirm authenticator enrollment with a current TOTP code. */
export const confirmTwoFactorSetup = code => http.post('/auth/2fa/confirm', { code })

/** Disable authenticator verification after rechecking the account password and TOTP. */
export const disableTwoFactor = (password, code) => {
  return http.post('/auth/2fa/disable', {
    password: encryptPassword(password),
    code
  })
}

/**
 * 用户注册
 * @param {string} username - 用户名
 * @param {string} nick - 昵称
 * @param {string} password - 密码（明文）
 * @param {string} mobile - 临时号码
 * @param {string} inviter - 邀请码
 * @param {string} turnstileToken - Cloudflare Turnstile 验证token
 * @returns {Promise}
 */
export const register = async (username, nick, password, mobile, inviter, turnstileToken) => {
  const encryptedPassword = encryptPassword(password)

  return http.post('/auth/register', {
    username,
    nickname: nick,
    password: encryptedPassword,
    mobile: mobile || username, // 如果没有临时号码，用用户名代替
    inviter: inviter,
    deviceFingerprint: await getDeviceFingerprint(),
    sessionId: getSessionId(),
    turnstileToken
  })
}

/**
 * 退出登录，清除所有登录信息
 */
export const logout = () => {
  return http.post('/auth/logout')
    .catch(error => {
      console.warn('退出登录接口调用失败，继续清除本地登录信息:', error)
    })
    .finally(() => {
      // 清除登录凭证
      localStorage.removeItem('tianqi-token')
      localStorage.removeItem('tianqi-user')
      sessionStorage.removeItem('tianqi-token')
      sessionStorage.removeItem('tianqi-user')
    })
}

/**
 * 保存用户信息
 * @param {Object} user - 用户信息
 * @param {string} token - 用户令牌
 * @returns {void}
 */
export const saveUserInfo = (user, token) => {
  localStorage.setItem('tianqi-token', token)
  localStorage.setItem('tianqi-user', JSON.stringify(user))
}

/**
 * 获取当前用户信息
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('tianqi-user')
  return userStr ? JSON.parse(userStr) : null
}

/**
 * 检查用户是否已登录
 * @returns {boolean} 用户是否已登录
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('tianqi-token')
  return !!token
}

/**
 * 更新用户密码
 * @param {string} newPwd - 新密码（明文）
 * @returns {Promise}
 */
export const updatePassword = async (newPwd) => {
  const encryptedNewPwd = encryptPassword(newPwd)

  return http.post('/auth/reset-password', {
    newPassword: encryptedNewPwd,
    deviceFingerprint: await getDeviceFingerprint(),
    sessionId: getSessionId()
  })
}

/**
 * 获取当前登录用户的API认证凭证
 * @returns {Promise} API凭证信息
 */
export const getApiCredentials = () => {
  return http.post('/system/user/getApiCredentials')
}

/**
 * 重置当前登录用户的API认证凭证
 * 注意：重置后会强制退出登录
 * @returns {Promise}
 */
export const resetApiCredentials = () => {
  return http.post('/system/user/resetApiCredentials')
}

/**
 * 获取API登录IP白名单配置
 */
export const getApiIpWhitelist = () => {
  return http.get('/system/user/getApiIpWhitelist')
}

/**
 * 独立保存API登录IP白名单配置
 */
export const updateApiIpWhitelist = (data) => {
  return http.post('/system/user/updateApiIpWhitelist', data)
}

/**
 * 获取当前请求IP
 * @returns {Promise}
 */
export const getCurrentIp = () => {
  return http.get('/system/user/currentIp')
}
